<template>
  <div class="screen-page">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>数据看板</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="screen-header tb-card">
      <div class="screen-title-wrap">
        <div>
          <h2 class="screen-title">运营数据大屏</h2>
          <p class="screen-desc">聚合订单、用户、财务和城市数据，快速掌握平台核心经营指标。</p>
        </div>
        <div class="screen-meta">
          <span class="screen-time">更新时间 {{ updateTime }}</span>
          <span class="screen-mode" :class="{ active: autoRefreshEnabled }">自动刷新 {{ autoRefreshEnabled ? '已开启' : '已关闭' }}</span>
          <el-switch v-model="autoRefreshEnabled" @change="handleAutoRefreshChange"></el-switch>
          <el-button size="small" type="primary" icon="el-icon-refresh" @click="loadDashboard">刷新数据</el-button>
          <el-button size="small" icon="el-icon-full-screen" @click="toggleFullscreen">{{ isFullscreen ? '退出全屏' : '全屏展示' }}</el-button>
        </div>
      </div>
    </div>

    <div class="screen-notice tb-card">
      <div class="notice-label">
        <i class="el-icon-message-solid"></i>
        <span>实时播报</span>
      </div>
      <div class="notice-marquee">
        <div class="notice-track">
          <span class="notice-item" v-for="(item, index) in broadcastItems" :key="item + '-' + index">{{ item }}</span>
        </div>
      </div>
      <div class="notice-count">{{ broadcastItems.length }} 条</div>
    </div>

    <div class="screen-toolbar tb-card">
      <div class="toolbar-left">
        <div class="toolbar-item">
          <span class="toolbar-label">城市</span>
          <el-select v-model="filterCity" size="small" placeholder="全部城市">
            <el-option label="全部城市" value=""></el-option>
            <el-option v-for="item in cityOptions" :key="item" :label="item" :value="item"></el-option>
          </el-select>
        </div>
        <div class="toolbar-item">
          <span class="toolbar-label">状态</span>
          <el-select v-model="filterStatus" size="small" placeholder="全部状态">
            <el-option label="全部状态" value="all"></el-option>
            <el-option label="待接单" :value="1"></el-option>
            <el-option label="待服务" :value="2"></el-option>
            <el-option label="服务中" :value="3"></el-option>
            <el-option label="待确认" :value="4"></el-option>
            <el-option label="待评价" :value="5"></el-option>
            <el-option label="已完成" :value="6"></el-option>
            <el-option label="已取消" :value="7"></el-option>
          </el-select>
        </div>
        <div class="toolbar-item toolbar-range">
          <span class="toolbar-label">时间</span>
          <div class="range-pills">
            <button
              v-for="item in rangeOptions"
              :key="item.value"
              type="button"
              class="range-pill"
              :class="{ active: filterRange === item.value }"
              @click="filterRange = item.value"
            >
              {{ item.label }}
            </button>
          </div>
        </div>
      </div>
      <div class="toolbar-right">
        <span class="toolbar-result">当前筛选：{{ activeFilterText }}</span>
        <el-button size="small" @click="resetFilters">重置筛选</el-button>
      </div>
    </div>

    <div class="screen-kpi">
      <div class="kpi-card tb-card" v-for="item in summaryCards" :key="item.key">
        <div class="kpi-label">{{ item.label }}</div>
        <div class="kpi-value">
          <count-number
            :value="item.numericValue"
            :prefix="item.prefix"
            :suffix="item.suffix"
            :decimals="item.decimals"
          />
        </div>
        <div class="kpi-footer">
          <span>{{ item.subLabel }}</span>
          <span class="kpi-tag" :class="'tag-' + item.deltaType">{{ item.delta }}</span>
        </div>
      </div>
    </div>

    <div class="screen-grid">
      <div class="screen-panel tb-card panel-large">
        <div class="panel-head">
          <span class="tb-section-title">近七日订单趋势</span>
          <span class="panel-extra">按日统计订单量</span>
        </div>
        <div ref="trendChart" class="chart-box chart-tall"></div>
      </div>

      <div class="screen-panel tb-card">
        <div class="panel-head">
          <span class="tb-section-title">订单状态分布</span>
          <span class="panel-extra">实时状态占比</span>
        </div>
        <div ref="statusChart" class="chart-box"></div>
      </div>

      <div class="screen-panel tb-card">
        <div class="panel-head">
          <span class="tb-section-title">城市订单排行</span>
          <span class="panel-extra">TOP 6</span>
        </div>
        <div ref="cityChart" class="chart-box"></div>
      </div>

      <div class="screen-panel tb-card panel-large">
        <div class="panel-head">
          <span class="tb-section-title">城市热力分布</span>
          <span class="panel-extra">核心城市活跃热度</span>
        </div>
        <div ref="heatChart" class="chart-box chart-map"></div>
      </div>

      <div class="screen-panel tb-card">
        <div class="panel-head">
          <span class="tb-section-title">今日经营快照</span>
          <span class="panel-extra">核心摘要</span>
        </div>
        <div class="snapshot-list">
          <div class="snapshot-item" v-for="item in snapshots" :key="item.label">
            <span class="snapshot-label">{{ item.label }}</span>
            <span class="snapshot-value">{{ item.value }}</span>
          </div>
        </div>
      </div>

      <div class="screen-panel tb-card">
        <div class="panel-head">
          <span class="tb-section-title">业务类型分布</span>
          <span class="panel-extra">按订单标题聚合</span>
        </div>
        <div class="service-rank">
          <div class="service-row" v-for="item in serviceCategoryData" :key="item.name">
            <div class="service-meta">
              <span class="service-name">{{ item.name }}</span>
              <span class="service-count">{{ item.value }} 单</span>
            </div>
            <div class="service-bar">
              <div class="service-bar-inner" :style="{ width: item.percent + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="screen-panel tb-card">
        <div class="panel-head">
          <span class="tb-section-title">告警中心</span>
          <span class="panel-extra">需优先关注</span>
        </div>
        <div class="alert-list">
          <div class="alert-item" v-for="item in alertItems" :key="item.title">
            <div class="alert-main">
              <span class="alert-dot" :class="'dot-' + item.level"></span>
              <div>
                <div class="alert-title">{{ item.title }}</div>
                <div class="alert-desc">{{ item.desc }}</div>
              </div>
            </div>
            <div class="alert-value" :class="'text-' + item.level">{{ item.value }}</div>
          </div>
        </div>
      </div>

      <div class="screen-panel tb-card">
        <div class="panel-head">
          <span class="tb-section-title">待处理事项</span>
          <span class="panel-extra">运营动作建议</span>
        </div>
        <div class="todo-list">
          <div class="todo-item" v-for="item in pendingTasks" :key="item.title">
            <div class="todo-index">{{ item.index }}</div>
            <div class="todo-main">
              <div class="todo-title">{{ item.title }}</div>
              <div class="todo-desc">{{ item.desc }}</div>
            </div>
            <div class="todo-tag">{{ item.tag }}</div>
          </div>
        </div>
      </div>

      <div class="screen-panel tb-card panel-wide">
        <div class="panel-head">
          <span class="tb-section-title">最新订单动态</span>
          <span class="panel-extra">最近 8 条订单</span>
        </div>
        <div class="order-list">
          <div class="order-item" v-for="item in recentOrders" :key="item.orderNumber">
            <div class="order-main">
              <div class="order-title">{{ item.title }}</div>
              <div class="order-sub">{{ item.orderNumber }}</div>
            </div>
            <div class="order-user">
              <div>{{ item.username }}</div>
              <div class="order-city">{{ item.cityName }}</div>
            </div>
            <div class="order-money">{{ item.moneyText }}</div>
            <div class="order-status" :class="'status-' + item.statusType">{{ item.statusText }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getDashboardData,
  getCityNameList
} from '../../api/userMG'

var CountNumber = {
  name: 'CountNumber',
  props: {
    value: {
      type: Number,
      default: 0
    },
    prefix: {
      type: String,
      default: ''
    },
    suffix: {
      type: String,
      default: ''
    },
    decimals: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      displayValue: 0,
      timer: null
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(next) {
        this.animateTo(Number(next || 0))
      }
    }
  },
  beforeDestroy() {
    if (this.timer) {
      cancelAnimationFrame(this.timer)
    }
  },
  methods: {
    animateTo(target) {
      var self = this
      var start = Number(this.displayValue || 0)
      var begin = performance.now()
      var duration = 720
      if (this.timer) {
        cancelAnimationFrame(this.timer)
      }
      var step = function(now) {
        var progress = Math.min((now - begin) / duration, 1)
        var eased = 1 - Math.pow(1 - progress, 3)
        self.displayValue = start + (target - start) * eased
        if (progress < 1) {
          self.timer = requestAnimationFrame(step)
        }
      }
      this.timer = requestAnimationFrame(step)
    },
    formatValue(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: this.decimals,
        maximumFractionDigits: this.decimals
      })
    }
  },
  template: '<span class="count-number"><span v-if="prefix" class="count-prefix">{{ prefix }}</span>{{ formatValue(displayValue) }}<span v-if="suffix" class="count-suffix">{{ suffix }}</span></span>'
}

export default {
  name: 'DashboardScreen',
  components: {
    CountNumber
  },
  data() {
    return {
      loading: false,
      updateTime: '',
      orderList: [],
      userTotal: 0,
      onlineTotal: 0,
      financeList: [],
      citySummary: null,
      overviewMetrics: {
        userTotal: 0,
        onlineUserTotal: 0,
        orderTotalAmount: 0,
        orderPaidAmount: 0,
        sellerIncome: 0,
        withdrawOut: 0,
        withdrawTotal: 0,
        platformCommission: 0,
        cancelFee: 0,
        visitFee: 0
      },
      cityOptionList: [],
      dashboardTrendData: [],
      dashboardStatusData: [],
      dashboardCityData: [],
      dashboardServiceData: [],
      dashboardAlerts: [],
      dashboardTodos: [],
      dashboardBroadcasts: [],
      dashboardSnapshots: [],
      chartInstances: {},
      autoRefreshEnabled: true,
      autoRefreshTimer: null,
      isFullscreen: false,
      filterCity: '',
      filterStatus: 'all',
      filterRange: '30d',
      rangeOptions: [
        { label: '近7天', value: '7d' },
        { label: '近15天', value: '15d' },
        { label: '近30天', value: '30d' },
        { label: '全部', value: 'all' }
      ]
    }
  },
  computed: {
    cityOptions() {
      if (this.cityOptionList.length) {
        return this.cityOptionList
      }
      return this.uniqueBy(this.orderList, 'cityName').map(function(item) {
        return item.cityName
      }).filter(Boolean)
    },
    filteredOrders() {
      var self = this
      return this.orderList.filter(function(item) {
        var matchCity = !self.filterCity || item.cityName === self.filterCity
        var matchStatus = self.filterStatus === 'all' || item.status === Number(self.filterStatus)
        var matchRange = self.matchesRange(item.createTime)
        return matchCity && matchStatus && matchRange
      })
    },
    activeFilterText() {
      var cityText = this.filterCity || '全部城市'
      var statusMap = {
        all: '全部状态',
        1: '待接单',
        2: '待服务',
        3: '服务中',
        4: '待确认',
        5: '待评价',
        6: '已完成',
        7: '已取消'
      }
      var rangeText = this.rangeOptions.find(function(item) {
        return item.value === this.filterRange
      }, this)
      return cityText + ' / ' + (statusMap[this.filterStatus] || '全部状态') + ' / ' + (rangeText ? rangeText.label : '全部')
    },
    summaryCards() {
      return [
        {
          key: 'users',
          label: '平台用户',
          numericValue: this.overviewMetrics.userTotal,
          prefix: '',
          suffix: '',
          decimals: 0,
          subLabel: '平台累计注册用户',
          delta: '真实接口返回',
          deltaType: 'neutral'
        },
        {
          key: 'online',
          label: '在线用户',
          numericValue: this.overviewMetrics.onlineUserTotal,
          prefix: '',
          suffix: '',
          decimals: 0,
          subLabel: '按平台在线规则统计',
          delta: this.getOnlineRateText(),
          deltaType: 'neutral'
        },
        {
          key: 'orderTotalAmount',
          label: '订单总额',
          numericValue: this.overviewMetrics.orderTotalAmount,
          prefix: '¥ ',
          suffix: '',
          decimals: 2,
          subLabel: '城市/时间维度真实汇总',
          delta: '订单金额汇总',
          deltaType: 'neutral'
        },
        {
          key: 'orderPaidAmount',
          label: '实付金额',
          numericValue: this.overviewMetrics.orderPaidAmount,
          prefix: '¥ ',
          suffix: '',
          decimals: 2,
          subLabel: '已支付订单金额',
          delta: '已支付真实金额',
          deltaType: 'neutral'
        },
        {
          key: 'platformCommission',
          label: '平台佣金',
          numericValue: this.overviewMetrics.platformCommission,
          prefix: '¥ ',
          suffix: '',
          decimals: 2,
          subLabel: '平台服务佣金收入',
          delta: '平台收入口径',
          deltaType: 'neutral'
        }
      ]
    },
    recentOrders() {
      return this.filteredOrders.slice(0, 8)
    },
    alertItems() {
      if (this.dashboardAlerts.length) {
        return this.dashboardAlerts
      }
      var refundCount = this.filteredOrders.filter(function(item) {
        return item.statusType === 'warning' || item.statusType === 'danger'
      }).length
      var pendingCount = this.getStatusCount('pending')
      var runningCount = this.getStatusCount('running')
      var onlineRate = this.getOnlineRateValue()
      return [
        {
          title: '退款订单预警',
          desc: '含退款中与已退款订单，需要重点复核售后与争议情况',
          value: refundCount + ' 单',
          level: refundCount > 2 ? 'high' : 'medium'
        },
        {
          title: '待处理订单积压',
          desc: '新单未及时响应会直接影响转化率与用户体验',
          value: pendingCount + ' 单',
          level: pendingCount > 2 ? 'high' : 'medium'
        },
        {
          title: '执行中项目监控',
          desc: '进行中的服务订单建议按城市和服务商分批巡检',
          value: runningCount + ' 单',
          level: 'medium'
        },
        {
          title: '在线用户活跃率',
          desc: '当前在线占总用户比值，可作为流量活跃观察指标',
          value: onlineRate + '%',
          level: this.onlineTotal > 300 ? 'low' : 'medium'
        }
      ]
    },
    pendingTasks() {
      if (this.dashboardTodos.length) {
        return this.dashboardTodos
      }
      var topCity = this.buildCityData()[0]
      var topCityText = topCity ? topCity.name : '当前筛选城市'
      return [
        {
          index: '01',
          title: '优先处理退款预警订单',
          desc: '建议先核对近 24 小时退款单的处理节点与沟通记录',
          tag: '高优'
        },
        {
          index: '02',
          title: '跟进待处理新单转化',
          desc: '对积压订单安排客服或运营 10 分钟内完成首次触达',
          tag: '处理中'
        },
        {
          index: '03',
          title: '检查高活跃城市供给',
          desc: topCityText + ' 当前订单活跃度较高，建议补充服务商与资源位',
          tag: '关注'
        },
        {
          index: '04',
          title: '复盘今日经营波动原因',
          desc: '结合趋势图与城市热力分布，定位流量和订单波动来源',
          tag: '复盘'
        }
      ]
    },
    broadcastItems() {
      if (this.dashboardBroadcasts.length) {
        return this.dashboardBroadcasts.concat(this.dashboardBroadcasts.slice(0, 3))
      }
      var items = this.recentOrders.slice(0, 6).map(function(item) {
        return item.cityName + ' · ' + item.username + ' 提交「' + item.title + '」，当前状态：' + item.statusText
      })
      if (!items.length) {
        items = ['当前筛选条件下暂无实时订单动态']
      }
      return items.concat(items.slice(0, 3))
    },
    serviceCategoryData() {
      if (this.dashboardServiceData.length) {
        var total = this.dashboardServiceData.reduce(function(sum, item) {
          return sum + Number(item.value || 0)
        }, 0) || 1
        return this.dashboardServiceData.map(function(item) {
          return {
            name: item.name,
            value: item.value,
            percent: Math.round((Number(item.value || 0) / total) * 100)
          }
        })
      }
      var total = this.filteredOrders.length || 1
      var source = this.buildServiceCategoryData()
      return source.map(function(item) {
        return {
          name: item.name,
          value: item.value,
          percent: Math.round((item.value / total) * 100)
        }
      })
    },
    snapshots() {
      if (this.dashboardSnapshots.length) {
        return this.dashboardSnapshots
      }
      var refundCount = this.filteredOrders.filter(function(item) {
        return item.statusText.indexOf('退款') > -1
      }).length
      var cityCount = this.uniqueBy(this.filteredOrders, 'cityName').length
      return [
        { label: '覆盖城市', value: this.formatNumber(cityCount || this.extractCityCount()) },
        { label: '提现支出', value: this.formatMoney(this.overviewMetrics.withdrawOut) },
        { label: '完成订单', value: this.formatNumber(this.getStatusCount('success')) + ' 单' },
        { label: '取消赔付', value: this.formatMoney(this.overviewMetrics.cancelFee) },
        { label: '上门费用', value: this.formatMoney(this.overviewMetrics.visitFee) },
        { label: '退款相关', value: this.formatNumber(refundCount) + ' 单' }
      ]
    }
  },
  watch: {
    filterCity() {
      this.loadDashboard()
    },
    filterStatus() {
      this.loadDashboard()
    },
    filterRange() {
      this.loadDashboard()
    },
    filteredOrders() {
      this.$nextTick(this.renderCharts)
    }
  },
  mounted() {
    this.loadCityOptions()
    this.loadDashboard()
    this.initAutoRefreshTimer()
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    this.clearAutoRefreshTimer()
    this.disposeCharts()
  },
  methods: {
    loadCityOptions() {
      var self = this
      getCityNameList().then(function(res) {
        if (res && res.code === 200 && res.data && Array.isArray(res.data.list)) {
          self.cityOptionList = res.data.list.map(function(item) {
            return item.cityName || item.name || item
          }).filter(Boolean)
        }
      }).catch(function() {})
    },
    loadDashboard() {
      var self = this
      self.loading = true
      self.updateTime = self.formatDateTime(new Date())
      getDashboardData({
          cityName: self.filterCity,
          beginTime: self.getBeginTime(),
          endTime: self.getEndTime(),
          status: self.getOrderStatusParam()
        }).then(function(res) {
        self.applyApiData(res)
        self.$nextTick(function() {
          self.renderCharts()
        })
      }).finally(function() {
        self.loading = false
      })
    },
    applyApiData(res) {
      var data = res && res.code === 200 && res.data ? res.data : {}
      this.orderList = this.normalizeOrders({ code: 200, data: { list: data.orders || [] } })
      this.financeList = data.financeList || []
      this.citySummary = data.cityList || []
      this.overviewMetrics = this.normalizeOverviewMetrics(data.overview || {})
      this.userTotal = this.overviewMetrics.userTotal
      this.onlineTotal = this.overviewMetrics.onlineUserTotal
      this.dashboardTrendData = data.trend || []
      this.dashboardStatusData = data.statusList || []
      this.dashboardCityData = data.cityList || []
      this.dashboardServiceData = data.serviceList || []
      this.dashboardAlerts = data.alerts || []
      this.dashboardTodos = data.todos || []
      this.dashboardBroadcasts = data.broadcasts || []
      this.dashboardSnapshots = (data.snapshots || []).map(function(item) {
        return {
          label: item.label || item.title,
          value: item.value
        }
      })
    },
    normalizeOrders(orderRes) {
      if (!orderRes || orderRes.code !== 200 || !orderRes.data) {
        return []
      }
      var list = orderRes.data.orderEntityList || orderRes.data.list || []
      var self = this
      return list.map(function(item, index) {
        var statusInfo = self.getOrderStatusMeta(item.status)
        var buyer = item.memberBuyer || {}
        var title = self.getOrderTitle(item)
        return {
          orderNumber: item.orderNumber || 'ORDER-' + index,
          title: title,
          username: buyer.username || item.username || item.nickName || '匿名用户',
          cityName: item.cityName || item.city || '未分配',
          money: self.getMoneyValue(item.payPrice || item.basicCost || item.money || item.goodsPrice || item.price || 0),
          moneyText: self.formatMoney(item.payPrice || item.basicCost || item.money || item.goodsPrice || item.price || 0),
          status: item.status,
          statusText: statusInfo.text,
          statusType: statusInfo.type,
          createTime: item.createTime || item.addTime || ''
        }
      })
    },
    normalizeFinance(financeRes) {
      if (!financeRes || financeRes.code !== 200 || !financeRes.data) {
        return []
      }
      return financeRes.data.list || []
    },
    normalizeOverviewMetrics(source) {
      source = source || {}
      return {
        userTotal: Number(source.userTotal || 0),
        onlineUserTotal: Number(source.onlineUserTotal || 0),
        orderTotalAmount: Number(source.orderTotalAmount || 0),
        orderPaidAmount: Number(source.orderPaidAmount || 0),
        sellerIncome: Number(source.sellerIncome || 0),
        withdrawOut: Number(source.withdrawOut || 0),
        withdrawTotal: Number(source.withdrawTotal || 0),
        platformCommission: Number(source.platformCommission || 0),
        cancelFee: Number(source.cancelFee || 0),
        visitFee: Number(source.visitFee || 0)
      }
    },
    extractCount(res, fallback) {
      if (!res || res.code !== 200 || !res.data) {
        return fallback || 0
      }
      return Number(res.data.count || res.data.total || (res.data.list && res.data.list.length) || fallback || 0)
    },
    getOrderTitle(item) {
      if (item.skillData) {
        try {
          var skillData = typeof item.skillData === 'string' ? JSON.parse(item.skillData) : item.skillData
          if (skillData && skillData.title) {
            return skillData.title
          }
          if (skillData && skillData.name) {
            return skillData.name
          }
        } catch (e) {}
      }
      return item.title || item.goodsTitle || '平台订单'
    },
    extractCityCount() {
      if (!this.citySummary) {
        return 0
      }
      if (Array.isArray(this.citySummary.list)) {
        return this.citySummary.list.length
      }
      if (Array.isArray(this.citySummary.cityList)) {
        return this.citySummary.cityList.length
      }
      return 0
    },
    getMoneyValue(value) {
      var num = Number(value || 0)
      if (num > 9999) {
        return num / 100
      }
      return num
    },
    formatMoney(value) {
      var amount = this.getMoneyValue(value)
      return '¥ ' + amount.toFixed(2)
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString()
    },
    getOnlineRateValue() {
      if (!this.overviewMetrics.userTotal) {
        return '0.0'
      }
      return ((this.overviewMetrics.onlineUserTotal / this.overviewMetrics.userTotal) * 100).toFixed(1)
    },
    getOnlineRateText() {
      return '在线率 ' + this.getOnlineRateValue() + '%'
    },
    formatDateTime(date) {
      var d = date instanceof Date ? date : new Date(date)
      var pad = function(n) {
        return n < 10 ? '0' + n : '' + n
      }
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
    },
    getOrderStatusMeta(status) {
      var map = {
        1: { text: '待接单', type: 'pending' },
        2: { text: '待服务', type: 'pending' },
        3: { text: '服务中', type: 'running' },
        4: { text: '待确认', type: 'running' },
        5: { text: '待评价', type: 'running' },
        6: { text: '已完成', type: 'success' },
        7: { text: '已取消', type: 'danger' }
      }
      return map[status] || { text: '处理中', type: 'running' }
    },
    getStatusCount(type) {
      return this.filteredOrders.filter(function(item) {
        return item.statusType === type
      }).length
    },
    resetFilters() {
      this.filterCity = ''
      this.filterStatus = 'all'
      this.filterRange = '30d'
    },
    getOrderStatusParam() {
      return this.filterStatus === 'all' ? -1 : Number(this.filterStatus)
    },
    getBeginTime() {
      if (this.filterRange === 'all') {
        return 0
      }
      var dayMap = {
        '7d': 7,
        '15d': 15,
        '30d': 30
      }
      var days = dayMap[this.filterRange] || 30
      return parseInt((Date.now() - days * 24 * 60 * 60 * 1000) / 1000, 10)
    },
    getEndTime() {
      return parseInt(Date.now() / 1000, 10)
    },
    matchesRange(value) {
      if (this.filterRange === 'all') {
        return true
      }
      var time = this.getTimestamp(value)
      if (!time) {
        return true
      }
      var now = Date.now()
      var dayMap = {
        '7d': 7,
        '15d': 15,
        '30d': 30
      }
      var days = dayMap[this.filterRange] || 30
      return now - time <= days * 24 * 60 * 60 * 1000
    },
    getTimestamp(value) {
      if (!value) {
        return 0
      }
      var num = Number(value)
      if (!Number.isNaN(num) && num > 0) {
        return num > 9999999999 ? num : num * 1000
      }
      var date = new Date(value)
      return Number.isNaN(date.getTime()) ? 0 : date.getTime()
    },
    uniqueBy(list, field) {
      var map = {}
      return list.filter(function(item) {
        if (!item[field]) {
          return false
        }
        if (map[item[field]]) {
          return false
        }
        map[item[field]] = true
        return true
      })
    },
    buildTrendData() {
      if (this.dashboardTrendData.length) {
        return {
          labels: this.dashboardTrendData.map(function(item) { return item.name }),
          values: this.dashboardTrendData.map(function(item) { return item.value })
        }
      }
      var result = {}
      var self = this
      this.filteredOrders.forEach(function(item, index) {
        var label = self.getDateLabel(item.createTime, index)
        result[label] = (result[label] || 0) + 1
      })
      var labels = Object.keys(result)
      labels.sort()
      return {
        labels: labels,
        values: labels.map(function(key) { return result[key] })
      }
    },
    getDateLabel(value, index) {
      if (!value) {
        var d = new Date()
        d.setDate(d.getDate() - (index % 7))
        return (d.getMonth() + 1) + '/' + d.getDate()
      }
      var date = new Date(Number(value) > 9999999999 ? Number(value) : Number(value) * 1000)
      if (isNaN(date.getTime())) {
        date = new Date(value)
      }
      if (isNaN(date.getTime())) {
        return '近' + (index + 1) + '日'
      }
      return (date.getMonth() + 1) + '/' + date.getDate()
    },
    buildStatusData() {
      if (this.dashboardStatusData.length) {
        return this.dashboardStatusData
      }
      var counter = {
        待接单: 0,
        待服务: 0,
        服务中: 0,
        待确认: 0,
        待评价: 0,
        已完成: 0,
        已取消: 0
      }
      this.filteredOrders.forEach(function(item) {
        counter[item.statusText] = (counter[item.statusText] || 0) + 1
      })
      return Object.keys(counter).map(function(key) {
        return { name: key, value: counter[key] }
      }).filter(function(item) {
        return item.value > 0
      })
    },
    buildCityData() {
      if (this.dashboardCityData.length) {
        return this.dashboardCityData
      }
      var counter = {}
      this.filteredOrders.forEach(function(item) {
        var key = item.cityName || '未分配'
        counter[key] = (counter[key] || 0) + 1
      })
      return Object.keys(counter).map(function(key) {
        return { name: key, value: counter[key] }
      }).sort(function(a, b) {
        return b.value - a.value
      }).slice(0, 6)
    },
    buildServiceCategoryData() {
      if (this.dashboardServiceData.length) {
        return this.dashboardServiceData
      }
      var counter = {
        设计服务: 0,
        视频剪辑: 0,
        运营策划: 0,
        开发实施: 0,
        文案咨询: 0,
        其他业务: 0
      }
      this.filteredOrders.forEach(function(item) {
        var title = item.title || ''
        if (/设计|海报|封面|详情页|品牌/.test(title)) {
          counter['设计服务'] += 1
        } else if (/视频|剪辑/.test(title)) {
          counter['视频剪辑'] += 1
        } else if (/运营|方案|策划/.test(title)) {
          counter['运营策划'] += 1
        } else if (/开发|程序|H5|小程序/.test(title)) {
          counter['开发实施'] += 1
        } else if (/文案|咨询/.test(title)) {
          counter['文案咨询'] += 1
        } else {
          counter['其他业务'] += 1
        }
      })
      return Object.keys(counter).map(function(key) {
        return { name: key, value: counter[key] }
      }).filter(function(item) {
        return item.value > 0
      }).sort(function(a, b) {
        return b.value - a.value
      })
    },
    renderCharts() {
      this.renderTrendChart()
      this.renderStatusChart()
      this.renderCityChart()
      this.renderHeatChart()
    },
    renderTrendChart() {
      var trend = this.buildTrendData()
      var chart = this.getChart('trendChart')
      chart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 20, right: 20, top: 36, bottom: 24, containLabel: true },
        xAxis: {
          type: 'category',
          data: trend.labels,
          axisLine: { lineStyle: { color: '#d6e4ff' } },
          axisLabel: { color: '#7f8fa8' }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: 'rgba(79, 140, 255, 0.12)' } },
          axisLine: { show: false },
          axisLabel: { color: '#7f8fa8' }
        },
        series: [{
          name: '订单量',
          type: 'line',
          smooth: true,
          symbolSize: 8,
          data: trend.values,
          lineStyle: { color: '#409eff', width: 3 },
          itemStyle: { color: '#2563eb' },
          areaStyle: {
            color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(64, 158, 255, 0.28)' },
              { offset: 1, color: 'rgba(64, 158, 255, 0.04)' }
            ])
          }
        }]
      })
    },
    renderStatusChart() {
      var data = this.buildStatusData()
      var chart = this.getChart('statusChart')
      chart.setOption({
        tooltip: { trigger: 'item' },
        legend: { bottom: 0, textStyle: { color: '#7f8fa8' } },
        series: [{
          type: 'pie',
          radius: ['46%', '72%'],
          center: ['50%', '46%'],
          avoidLabelOverlap: false,
          label: { color: '#5d7090' },
          itemStyle: {
            borderColor: '#ffffff',
            borderWidth: 4
          },
          data: data.length ? data : [{ name: '暂无数据', value: 1 }],
          color: ['#409eff', '#67c23a', '#2563eb', '#e6a23c', '#f56c6c']
        }]
      })
    },
    renderCityChart() {
      var data = this.buildCityData()
      var chart = this.getChart('cityChart')
      chart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 20, right: 20, top: 20, bottom: 20, containLabel: true },
        xAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: 'rgba(79, 140, 255, 0.10)' } },
          axisLabel: { color: '#7f8fa8' }
        },
        yAxis: {
          type: 'category',
          data: data.map(function(item) { return item.name }),
          axisLabel: { color: '#5d7090' },
          axisLine: { show: false }
        },
        series: [{
          type: 'bar',
          data: data.map(function(item) { return item.value }),
          barWidth: 14,
          itemStyle: {
            borderRadius: [0, 8, 8, 0],
            color: new this.$echarts.graphic.LinearGradient(1, 0, 0, 0, [
              { offset: 0, color: '#69b1ff' },
              { offset: 1, color: '#2563eb' }
            ])
          }
        }]
      })
    },
    renderHeatChart() {
      var data = this.buildHeatData()
      var chart = this.getChart('heatChart')
      chart.setOption({
        grid: { left: 12, right: 12, top: 8, bottom: 8, containLabel: false },
        tooltip: {
          trigger: 'item',
          formatter: function(params) {
            return params.data.city + '<br/>活跃订单：' + params.data.value[2]
          }
        },
        xAxis: {
          min: 0,
          max: 100,
          show: false,
          splitLine: { show: false }
        },
        yAxis: {
          min: 0,
          max: 100,
          show: false,
          splitLine: { show: false }
        },
        graphic: [
          {
            type: 'group',
            left: 'center',
            top: 'middle',
            children: [
              {
                type: 'rect',
                shape: { x: -170, y: -95, width: 340, height: 190, r: 26 },
                style: {
                  fill: 'rgba(64, 158, 255, 0.05)',
                  stroke: 'rgba(79, 140, 255, 0.16)',
                  lineWidth: 1
                }
              },
              {
                type: 'polyline',
                shape: {
                  points: [[-120, 60], [-75, 20], [-40, 10], [0, -28], [52, -12], [94, -38], [120, 8], [88, 42], [38, 62], [-20, 70], [-75, 82]]
                },
                style: {
                  stroke: 'rgba(64, 158, 255, 0.28)',
                  lineWidth: 2
                }
              }
            ]
          }
        ],
        series: [
          {
            type: 'scatter',
            coordinateSystem: 'cartesian2d',
            symbolSize: function(val) {
              return Math.max(16, val[2] * 4 + 10)
            },
            itemStyle: {
              color: 'rgba(64, 158, 255, 0.26)',
              borderColor: '#409eff',
              borderWidth: 1.5,
              shadowBlur: 12,
              shadowColor: 'rgba(64, 158, 255, 0.18)'
            },
            label: {
              show: true,
              formatter: function(params) {
                return params.data.city
              },
              color: '#4b5d79',
              fontSize: 12,
              position: 'top'
            },
            data: data
          },
          {
            type: 'effectScatter',
            coordinateSystem: 'cartesian2d',
            rippleEffect: {
              scale: 4,
              brushType: 'stroke'
            },
            symbolSize: function(val) {
              return Math.max(12, val[2] * 3 + 8)
            },
            itemStyle: {
              color: '#2563eb'
            },
            data: data.slice(0, 4)
          }
        ]
      })
    },
    buildHeatData() {
      var cityPositionMap = {
        北京: [76, 74],
        上海: [86, 50],
        广州: [72, 26],
        深圳: [76, 22],
        杭州: [82, 46],
        成都: [48, 42],
        重庆: [52, 34],
        武汉: [66, 42],
        西安: [56, 54],
        苏州: [84, 48],
        长沙: [66, 30]
      }
      var cityData = this.buildCityData()
      if (!cityData.length) {
        cityData = [
          { name: '深圳', value: 12 },
          { name: '广州', value: 9 },
          { name: '上海', value: 8 },
          { name: '杭州', value: 7 },
          { name: '北京', value: 6 },
          { name: '成都', value: 5 }
        ]
      }
      return cityData.map(function(item) {
        var pos = cityPositionMap[item.name] || [50, 50]
        return {
          name: item.name,
          city: item.name,
          value: [pos[0], pos[1], item.value]
        }
      })
    },
    getChart(refName) {
      if (!this.chartInstances[refName]) {
        this.chartInstances[refName] = this.$echarts.init(this.$refs[refName])
      }
      return this.chartInstances[refName]
    },
    handleResize() {
      var self = this
      Object.keys(this.chartInstances).forEach(function(key) {
        if (self.chartInstances[key]) {
          self.chartInstances[key].resize()
        }
      })
    },
    initAutoRefreshTimer() {
      var self = this
      this.clearAutoRefreshTimer()
      if (!this.autoRefreshEnabled) {
        return
      }
      this.autoRefreshTimer = setInterval(function() {
        self.loadDashboard()
      }, 60000)
    },
    clearAutoRefreshTimer() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },
    handleAutoRefreshChange() {
      this.initAutoRefreshTimer()
    },
    toggleFullscreen() {
      var el = this.$el
      if (!document.fullscreenElement) {
        if (el.requestFullscreen) {
          el.requestFullscreen()
        }
      } else if (document.exitFullscreen) {
        document.exitFullscreen()
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
      this.$nextTick(this.handleResize)
    },
    disposeCharts() {
      var self = this
      Object.keys(this.chartInstances).forEach(function(key) {
        if (self.chartInstances[key]) {
          self.chartInstances[key].dispose()
        }
      })
      this.chartInstances = {}
    }
  }
}
</script>

<style scoped>
.screen-page {
  min-height: calc(100vh - 130px);
}
.screen-header {
  padding: 22px 24px;
  margin-bottom: 18px;
}
.screen-notice {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  margin-bottom: 18px;
  overflow: hidden;
}
.notice-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2563eb;
  font-weight: 700;
  font-size: 13px;
}
.notice-marquee {
  overflow: hidden;
  white-space: nowrap;
  position: relative;
}
.notice-track {
  display: inline-flex;
  align-items: center;
  gap: 24px;
  min-width: max-content;
  animation: screen-marquee 22s linear infinite;
}
.notice-item {
  color: #5d7090;
  font-size: 13px;
}
.notice-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(64, 158, 255, 0.08);
  color: #409eff;
  font-size: 12px;
  font-weight: 600;
}
.screen-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px;
  margin-bottom: 18px;
}
.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.toolbar-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.toolbar-label {
  color: #5d7090;
  font-size: 13px;
  font-weight: 600;
}
.toolbar-range {
  align-items: flex-start;
}
.range-pills {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.range-pill {
  height: 32px;
  padding: 0 14px;
  border: 1px solid rgba(79, 140, 255, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  color: #5d7090;
  cursor: pointer;
  transition: all .2s ease;
}
.range-pill:hover {
  color: #2563eb;
  border-color: rgba(64, 158, 255, 0.28);
}
.range-pill.active {
  color: #ffffff;
  border-color: transparent;
  background: linear-gradient(135deg, #2563eb, #409eff);
  box-shadow: 0 8px 16px rgba(37, 99, 235, 0.18);
}
.toolbar-result {
  color: #7f8fa8;
  font-size: 13px;
}
.screen-title-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}
.screen-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.2;
  color: #22324a;
}
.screen-desc {
  margin: 10px 0 0;
  color: #7f8fa8;
  font-size: 14px;
}
.screen-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.screen-time {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(64, 158, 255, 0.08);
  border: 1px solid rgba(79, 140, 255, 0.16);
  color: #409eff;
  font-size: 13px;
}
.count-number {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
}
.count-prefix,
.count-suffix {
  font-size: 18px;
  font-weight: 600;
  color: #4f6ea6;
}
.screen-mode {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.10);
  color: #7f8fa8;
  font-size: 12px;
  font-weight: 600;
}
.screen-mode.active {
  background: rgba(64, 158, 255, 0.10);
  color: #2563eb;
}
.screen-kpi {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}
.kpi-card {
  padding: 20px;
}
.kpi-label {
  color: #7f8fa8;
  font-size: 13px;
  margin-bottom: 14px;
}
.kpi-value {
  color: #22324a;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.kpi-footer {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #8fa2bd;
  font-size: 12px;
}
.kpi-tag {
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(64, 158, 255, 0.10);
  color: #2563eb;
  font-weight: 600;
}
.tag-up {
  background: rgba(103, 194, 58, 0.12);
  color: #67c23a;
}
.tag-down {
  background: rgba(245, 108, 108, 0.12);
  color: #f56c6c;
}
.tag-neutral {
  background: rgba(64, 158, 255, 0.10);
  color: #2563eb;
}
.screen-grid {
  display: grid;
  grid-template-columns: 1.45fr 1fr 1fr;
  gap: 16px;
}
.screen-panel {
  padding: 18px 18px 16px;
}
.panel-large {
  grid-column: 1 / 3;
}
.panel-wide {
  grid-column: 1 / 4;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.panel-extra {
  color: #90a2bc;
  font-size: 12px;
}
.chart-box {
  width: 100%;
  height: 280px;
}
.chart-tall {
  height: 320px;
}
.chart-map {
  height: 320px;
}
.snapshot-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.snapshot-item {
  padding: 18px 16px;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(245, 249, 255, 0.96));
  border: 1px solid rgba(79, 140, 255, 0.12);
}
.service-rank {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.service-row {
  padding: 12px 14px;
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(247, 250, 255, 0.94));
  border: 1px solid rgba(79, 140, 255, 0.10);
}
.service-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.service-name {
  color: #22324a;
  font-size: 13px;
  font-weight: 600;
}
.service-count {
  color: #7f8fa8;
  font-size: 12px;
}
.service-bar {
  height: 8px;
  border-radius: 999px;
  background: rgba(79, 140, 255, 0.08);
  overflow: hidden;
}
.service-bar-inner {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #2563eb, #69b1ff);
  box-shadow: 0 0 12px rgba(64, 158, 255, 0.18);
}
.alert-list,
.todo-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.alert-item,
.todo-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(247, 250, 255, 0.94));
  border: 1px solid rgba(79, 140, 255, 0.10);
}
.alert-item {
  justify-content: space-between;
}
.alert-main {
  display: flex;
  align-items: center;
  gap: 10px;
}
.alert-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-high {
  background: #f56c6c;
  box-shadow: 0 0 10px rgba(245, 108, 108, 0.24);
}
.dot-medium {
  background: #e6a23c;
  box-shadow: 0 0 10px rgba(230, 162, 60, 0.24);
}
.dot-low {
  background: #67c23a;
  box-shadow: 0 0 10px rgba(103, 194, 58, 0.24);
}
.alert-title,
.todo-title {
  color: #22324a;
  font-size: 13px;
  font-weight: 600;
}
.alert-desc,
.todo-desc {
  color: #8fa2bd;
  font-size: 12px;
  margin-top: 4px;
}
.alert-value {
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}
.text-high {
  color: #f56c6c;
}
.text-medium {
  color: #e6a23c;
}
.text-low {
  color: #67c23a;
}
.todo-item {
  justify-content: flex-start;
}
.todo-index {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(64, 158, 255, 0.10);
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.todo-main {
  flex: 1;
}
.todo-tag {
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(64, 158, 255, 0.08);
  color: #409eff;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}
.snapshot-label {
  display: block;
  color: #7f8fa8;
  font-size: 13px;
}
.snapshot-value {
  display: block;
  margin-top: 10px;
  color: #22324a;
  font-size: 24px;
  font-weight: 700;
}
.order-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.order-item {
  display: grid;
  grid-template-columns: 1.8fr 1fr 0.8fr 0.7fr;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(247, 250, 255, 0.94));
  border: 1px solid rgba(79, 140, 255, 0.10);
}
.order-title {
  color: #22324a;
  font-weight: 600;
}
.order-sub,
.order-city {
  color: #8fa2bd;
  font-size: 12px;
  margin-top: 4px;
}
.order-user,
.order-money {
  color: #4b5d79;
  font-weight: 500;
}
.order-status {
  justify-self: start;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
.status-pending {
  color: #e6a23c;
  background: rgba(230, 162, 60, 0.12);
}
.status-running {
  color: #409eff;
  background: rgba(64, 158, 255, 0.12);
}
.status-success {
  color: #67c23a;
  background: rgba(103, 194, 58, 0.12);
}
.status-warning {
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.10);
}
.status-danger {
  color: #ff5e7a;
  background: rgba(255, 94, 122, 0.10);
}

@media (max-width: 1440px) {
  .screen-kpi {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .screen-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .panel-large,
  .panel-wide {
    grid-column: auto;
  }
}

@media (max-width: 960px) {
  .screen-notice {
    grid-template-columns: 1fr;
  }
  .screen-toolbar,
  .toolbar-left,
  .toolbar-right,
  .toolbar-item {
    flex-direction: column;
    align-items: flex-start;
  }
  .screen-title-wrap,
  .screen-meta {
    flex-direction: column;
    align-items: flex-start;
  }
  .screen-kpi,
  .screen-grid,
  .snapshot-list {
    grid-template-columns: 1fr;
  }
  .order-item {
    grid-template-columns: 1fr;
  }
}

@keyframes screen-marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/dashboard/Screen.vue